import React from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import { useDateFormat } from "../../../utils/hooks/useDateFormat";
import moment from "../../../moment";
import Tag from "../../../components/Tag";
import { PAGE_VIEW_USER } from "../routes";
import { PAGE_DOM_DOCUMENT } from "../../Dom/routes";

const hexToRgba = (hex, alpha) => {
  const r = Number.parseInt(hex.slice(1, 3), 16);
  const g = Number.parseInt(hex.slice(3, 5), 16);
  const b = Number.parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const TrainingLabelBadge = ({ label }) => {
  if (!label) return null;
  return (
    <Tag
      style={{
        backgroundColor: hexToRgba(label.color, 0.15),
        color: "#000",
        fontWeight: 400,
        borderColor: label.color,
        borderWidth: 2
      }}
      className="ml-5"
    >
      {label.name}
    </Tag>
  );
};

/**
 * The related record of a notification can be missing when it was deleted after the notification was created
 * (the API include then returns null), so the employee is shown as plain text instead of a link that would
 * point nowhere.
 */
const EmployeeName = ({ employee, staffNumber }) => {
  const name = [employee?.last_name, employee?.first_name].filter(Boolean).join(" ") || "-";
  const label = [name, staffNumber].filter(Boolean).join(" ");

  if (!employee?.id) {
    return <span className="text-muted mr-5">{label}</span>;
  }

  return (
    <NavLink to={`${PAGE_VIEW_USER}/${employee.id}`} target="_blank" className="mr-5">
      {label}
    </NavLink>
  );
};

const prepareMessage = (data, dateFormat, trans) => {
  const { translation_key, qualification, employee, report, document } = data || {};
  const langKeyPart1 = `container.notifications_page.${translation_key}.message_part1`;
  const langKeyPart2 = `container.notifications_page.${translation_key}.message_part2`;
  const langKeyPart3 = `container.notifications_page.${translation_key}.message_part3`;
  const date = data ? moment(data.expiring_soon_date).format(dateFormat) : null;
  const staffNumber = employee?.userDetail ? `[${employee.userDetail.staff_number}]` : "";

  switch (translation_key) {
    case "qualification_missing": {
      return (
        <>
          <span className="mr-5">{trans(langKeyPart1)}</span>
          <EmployeeName employee={employee} staffNumber={staffNumber} />
          <span className="mr-5">{trans(langKeyPart2)}</span>
          <span className="text-muted mr-5">{data.training?.name || "-"}</span>
          <TrainingLabelBadge label={data.training?.label} />
        </>
      );
    }
    case "qualification_expired": {
      return (
        <>
          <span className="mr-5">{trans(langKeyPart1)}</span>
          <span className="text-muted mr-5">{qualification?.training?.name || "-"}</span>
          <span className="mr-5">{trans(langKeyPart2)}</span>
          <EmployeeName employee={employee} staffNumber={staffNumber} />
          <span className="mr-5">{trans(langKeyPart3)}</span>
          <TrainingLabelBadge label={qualification?.training?.label} />
        </>
      );
    }
    case "qualification_expiring_soon": {
      return (
        <>
          <span className="mr-5">{trans(langKeyPart1)}</span>
          <span className="text-muted mr-5">{qualification?.training?.name || "-"}</span>
          <span className="mr-5">{trans(langKeyPart2)}</span>
          <EmployeeName employee={employee} staffNumber={staffNumber} />
          <span className="mr-5">{trans(langKeyPart3)}</span>
          <span className="text-muted mr-5">{date}</span>
          <TrainingLabelBadge label={qualification?.training?.label} />
        </>
      );
    }
    case "export_ready": {
      return (
        <>
          <span className="mr-5">{trans(langKeyPart1)}</span>
          <NavLink to="/adm/exports-in-queue" target="_blank" className="mr-5">
            {report?.name || "-"}
          </NavLink>
          <span>{trans(langKeyPart2)}</span>
        </>
      );
    }
    case "notify_approver": {
      return (
        <>
          <span className="mr-5">{trans(langKeyPart1)}</span>
          {document?.id ? (
            <NavLink to={`${PAGE_DOM_DOCUMENT}/${document.id}`} target="_blank" className="mr-5">
              {document.title}
            </NavLink>
          ) : (
            <span className="text-muted mr-5">{document?.title || "-"}</span>
          )}
          <span className="mr-5">{trans(langKeyPart2)}</span>
          <EmployeeName employee={employee} staffNumber={staffNumber} />
        </>
      );
    }
    default: {
      return null;
    }
  }
};

const NotificationsMessageColumn = ({ data }) => {
  const { appFormat } = useDateFormat();
  const { t } = useTranslation();
  return prepareMessage(data, appFormat, t);
};

export default NotificationsMessageColumn;
